import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import { useTheme } from '../../../hooks/useTheme';
import { makeStyles } from './ScreenSelectionModal.styles';
import Icon from '../../Icon/Icon';
import { Screen } from '../../../utils/localComms/interfaces/localComms';

export interface ScreenRowProps extends Screen {
  index: number;
  isRecommended: boolean;
  isDisabled?: boolean;
  onSelect: (index: number) => void;
}

const ScreenRow: React.FC<ScreenRowProps> = ({
  index,
  displayName,
  width,
  height,
  x,
  y,
  isPrimary,
  isCurrent,
  isRecommended,
  isDisabled = false,
  onSelect,
}) => {
  const { translate } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const statusStyles = useMemo(() => {
    if (isCurrent) {
      return {
        iconName: 'UilCheckCircle',
        iconColor: colors.green,
        borderColor: colors.green,
        backgroundColor: colors.greenLightest,
      };
    } else if (isRecommended) {
      return {
        iconName: 'UilStar',
        iconColor: colors.states.focus,
        borderColor: colors.states.focus,
        backgroundColor: colors.orangeLightest,
      };
    }
    return {
      iconName: 'UilDesktop',
      iconColor: colors.disclosure,
      borderColor: colors.transparent,
      backgroundColor: colors.elevation1,
    };
  }, [isCurrent, isRecommended, colors]);

  const badges = [];
  if (isCurrent) {
    badges.push(translate('screenSelection.currentScreen'));
  }
  if (isPrimary) {
    badges.push(translate('screenSelection.primary'));
  }
  if (isRecommended) {
    badges.push(translate('screenSelection.recommended'));
  }

  // Build accessibility label with full screen description
  const accessibilityLabel = [
    displayName,
    translate('screenSelection.dimensions', { width, height }),
    isCurrent && translate('screenSelection.currentlySelected'),
    isRecommended && translate('screenSelection.recommendedFlag'),
    isPrimary && translate('screenSelection.primaryDisplay'),
  ]
    .filter(Boolean)
    .join(', ');

  const dynamicStyles = useMemo(
    () => ({
      borderColor: statusStyles.borderColor,
      backgroundColor: statusStyles.backgroundColor,
      opacity: isDisabled ? 0.6 : 1,
    }),
    [statusStyles.borderColor, statusStyles.backgroundColor, isDisabled],
  );

  return (
    <TouchableOpacity
      onPress={() => !isDisabled && onSelect(index)}
      disabled={isDisabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={translate('screenSelection.moveHint')}
      accessibilityState={{ disabled: isDisabled }}
      style={[styles.rowScreen, dynamicStyles]}
    >
      <View style={styles.screenInfo}>
        <Text style={styles.screenName}>{displayName}</Text>
        <Text style={styles.screenDetails}>
          {`${width} x ${height} @ ${x},${y}`}
        </Text>
        {badges.length > 0 && (
          <View style={styles.badgeContainer}>
            {badges.map((badge, i) => (
              <View key={i} style={styles.badge}>
                <Text style={styles.badgeText}>{badge}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
      <Icon
        size={24}
        name={statusStyles.iconName}
        color={statusStyles.iconColor}
        style={styles.icon}
      />
    </TouchableOpacity>
  );
};

export default ScreenRow;
